import { EmergencyContact, SafetyProtocol, SafeZone, GuideType } from '../types/emergency';

export const EMERGENCY_CONTACTS: EmergencyContact[] = [
  {
    id: '911',
    name: '911 Emergency Services',
    number: '911',
    type: 'universal',
    description: 'Police, Fire, Medical Emergency - Life-threatening situations',
    priority: 1,
    available24h: true,
    smsEnabled: false,
    category: 'emergency'
  },
  {
    id: 'poison-control',
    name: 'Poison Control Center',
    number: '1-800-222-1222',
    type: 'universal',
    description: '24/7 Poison Emergency Hotline - Free, confidential service',
    priority: 2,
    available24h: true,
    smsEnabled: false,
    category: 'health'
  },
  {
    id: 'crisis-text',
    name: 'Crisis Text Line',
    number: '741741',
    type: 'universal',
    description: 'Text HOME for mental health crisis support',
    priority: 3,
    available24h: true,
    smsEnabled: true,
    category: 'crisis'
  },
  {
    id: 'fema-disaster',
    name: 'FEMA Disaster Assistance',
    number: '1-800-621-3362',
    type: 'universal',
    description: 'Federal disaster relief and assistance programs',
    priority: 4,
    available24h: true,
    smsEnabled: false,
    category: 'disaster'
  },
  {
    id: 'red-cross',
    name: 'American Red Cross',
    number: '1-800-733-2767',
    type: 'universal',
    description: 'Disaster relief, emergency assistance, and shelter',
    priority: 5,
    available24h: true,
    smsEnabled: false,
    category: 'disaster'
  },
  {
    id: 'community-services',
    name: '211 Community Services',
    number: '211',
    type: 'universal',
    description: 'Local resources, food banks, shelters, and assistance',
    priority: 6,
    available24h: true,
    smsEnabled: true,
    category: 'emergency'
  },
  {
    id: 'suicide-prevention',
    name: 'Suicide & Crisis Lifeline',
    number: '988',
    type: 'universal',
    description: '24/7 suicide prevention and mental health crisis support',
    priority: 7,
    available24h: true,
    smsEnabled: true,
    category: 'crisis'
  },
  {
    id: 'domestic-violence',
    name: 'National Domestic Violence Hotline',
    number: '1-800-799-7233',
    type: 'universal',
    description: '24/7 confidential support for domestic violence victims',
    priority: 8,
    available24h: true,
    smsEnabled: true,
    category: 'crisis'
  }
];

export const SAFETY_PROTOCOLS: SafetyProtocol[] = [
  // Natural Disaster Protocols
  {
    id: 'earthquake-protocol',
    type: 'earthquake',
    title: 'Earthquake Emergency Response',
    description: 'Critical actions during and after an earthquake',
    priority: 'high',
    estimatedTime: '2-5 minutes',
    category: 'Natural Disaster',
    steps: [
      'DROP to hands and knees immediately',
      'Take COVER under a desk or table if available',
      'HOLD ON to your shelter and protect your head and neck',
      'Stay where you are until shaking stops completely',
      'Count to 60 after shaking stops before moving',
      'Exit building only if safe - watch for falling debris',
      'Stay away from damaged structures and power lines',
      'Check for injuries and provide first aid if trained',
      'Turn on battery-powered radio for emergency information',
      'Do not use elevators or light matches if you smell gas'
    ]
  },
  {
    id: 'wildfire-protocol',
    type: 'wildfire',
    title: 'Wildfire Evacuation Procedures',
    description: 'Essential steps for wildfire safety and evacuation',
    priority: 'high',
    estimatedTime: '10-30 minutes',
    category: 'Natural Disaster',
    steps: [
      'Monitor emergency alerts and evacuation orders continuously',
      'Prepare go-bag with essentials (documents, medications, water)',
      'Close all windows, doors, and vents to prevent ember entry',
      'Turn off gas utilities at the main valve',
      'Move flammable items away from windows',
      'Fill bathtubs and sinks with water for firefighting',
      'Follow designated evacuation routes - do not take shortcuts',
      'Keep vehicle windows closed and air conditioning on recirculate',
      'Do not return until authorities give official all-clear',
      'Stay informed through official emergency channels only'
    ]
  },
  {
    id: 'flood-protocol',
    type: 'flood',
    title: 'Flood Safety and Response',
    description: 'Life-saving actions during flood emergencies',
    priority: 'high',
    estimatedTime: '5-15 minutes',
    category: 'Natural Disaster',
    steps: [
      'Move to higher ground immediately - do not delay',
      'Never walk or drive through flood water (6 inches can knock you down)',
      'Turn off electricity at main breaker if safe to reach',
      'Do not drink or cook with flood water - it may be contaminated',
      'Signal for help from highest point if trapped',
      'Stay away from downed power lines in water',
      'Listen to emergency radio for evacuation instructions',
      'Wait for professional rescue if trapped - do not attempt self-rescue',
      'Avoid storm drains and moving water',
      'Have emergency supplies ready on upper floors'
    ]
  },
  {
    id: 'hurricane-protocol',
    type: 'hurricane',
    title: 'Hurricane Preparation and Safety',
    description: 'Comprehensive hurricane readiness plan',
    priority: 'high',
    estimatedTime: '30-60 minutes',
    category: 'Natural Disaster',
    steps: [
      'Monitor weather updates and evacuation zones',
      'Secure outdoor furniture and loose objects',
      'Board up windows with plywood or storm shutters',
      'Stock up on water (1 gallon per person per day for 7 days)',
      'Prepare non-perishable food for at least 7 days',
      'Fill vehicles with gas and check emergency supplies',
      'Charge all electronic devices and backup batteries',
      'Identify safe room on lowest floor away from windows',
      'Know evacuation routes and have multiple options',
      'Stay indoors during the storm - do not go outside during eye'
    ]
  },
  {
    id: 'tornado-protocol',
    type: 'tornado',
    title: 'Tornado Safety Procedures',
    description: 'Immediate actions when tornado threatens',
    priority: 'high',
    estimatedTime: '1-3 minutes',
    category: 'Natural Disaster',
    steps: [
      'Seek shelter immediately in basement or interior room',
      'Get to lowest floor and away from windows',
      'Go to center of building - avoid corners',
      'Get under heavy furniture or mattress for protection',
      'Cover head and neck with hands and arms',
      'Stay away from large roof areas like gyms or auditoriums',
      'If in vehicle, do not try to outrun tornado',
      'Exit vehicle and lie flat in low ditch if caught outside',
      'Do not hide under highway overpasses',
      'Listen for all-clear signal before leaving shelter'
    ]
  },
  {
    id: 'fire-protocol',
    type: 'fire',
    title: 'Building Fire Emergency',
    description: 'Fire escape and safety procedures',
    priority: 'high',
    estimatedTime: '2-5 minutes',
    category: 'Emergency Response',
    steps: [
      'Alert others immediately - shout "FIRE!"',
      'Call 911 as soon as you are safe',
      'Feel doors before opening - if hot, find another exit',
      'Stay low to avoid smoke - crawl if necessary',
      'Use stairs only - never use elevators',
      'Close doors behind you to slow fire spread',
      'Go to designated meeting point outside',
      'Do not re-enter building for any reason',
      'Account for all family members or coworkers',
      'Provide information to firefighters when they arrive'
    ]
  },

  // Human Health Protocols
  {
    id: 'infectious-disease-protocol',
    type: 'infectious_disease',
    title: 'Infectious Disease Outbreak Response',
    description: 'Protection measures during disease outbreaks',
    priority: 'high',
    estimatedTime: '15-30 minutes',
    category: 'Human Health',
    steps: [
      'Follow official health authority guidelines immediately',
      'Practice frequent hand washing with soap for 20+ seconds',
      'Wear appropriate personal protective equipment (PPE)',
      'Maintain recommended physical distancing measures',
      'Avoid large gatherings and crowded spaces',
      'Monitor your health and report symptoms to healthcare providers',
      'Isolate yourself if experiencing symptoms',
      'Keep emergency supplies including medications stocked',
      'Stay informed through official health channels only',
      'Seek medical attention if symptoms worsen'
    ]
  },
  {
    id: 'environmental-health-protocol',
    type: 'environmental_health',
    title: 'Environmental Health Emergency Response',
    description: 'Actions during air quality or environmental health crises',
    priority: 'medium',
    estimatedTime: '10-20 minutes',
    category: 'Human Health',
    steps: [
      'Stay indoors and close all windows and doors',
      'Turn off air conditioning and ventilation systems',
      'Use air purifiers if available',
      'Avoid outdoor activities and exercise',
      'Wear N95 masks when going outside if necessary',
      'Keep medications for respiratory conditions readily available',
      'Monitor air quality alerts and health advisories',
      'Seek medical attention if experiencing breathing difficulties',
      'Keep vulnerable family members (elderly, children) protected',
      'Follow evacuation orders if air quality becomes dangerous'
    ]
  },

  // War and Conflict Protocols
  {
    id: 'civil-unrest-protocol',
    type: 'civil_war',
    title: 'Civil Unrest and Conflict Safety',
    description: 'Personal safety during civil unrest or conflict',
    priority: 'high',
    estimatedTime: '5-15 minutes',
    category: 'War & Conflict',
    steps: [
      'Stay indoors and away from windows',
      'Avoid areas of known conflict or demonstration',
      'Keep emergency supplies and documents ready',
      'Maintain communication with family and friends',
      'Monitor official news sources for updates',
      'Have multiple evacuation routes planned',
      'Keep vehicles fueled and ready',
      'Avoid displaying political symbols or taking sides publicly',
      'Cooperate with law enforcement and military personnel',
      'Seek shelter in secure buildings if caught outside'
    ]
  },
  {
    id: 'terrorism-protocol',
    type: 'terrorism',
    title: 'Terrorism and Security Threat Response',
    description: 'Actions during terrorist incidents or security threats',
    priority: 'high',
    estimatedTime: '2-10 minutes',
    category: 'War & Conflict',
    steps: [
      'Run: Evacuate immediately if safe to do so',
      'Hide: Find secure location if evacuation is not possible',
      'Fight: As last resort, attempt to incapacitate threat',
      'Call 911 when safe to do so',
      'Follow instructions from law enforcement',
      'Avoid using elevators during evacuation',
      'Help others evacuate if possible',
      'Do not return to affected area until cleared by authorities',
      'Be aware of secondary attacks or devices',
      'Provide information to authorities about what you witnessed'
    ]
  },

  // Animal Health Protocols
  {
    id: 'animal-disease-protocol',
    type: 'wildlife_disease',
    title: 'Animal Disease Outbreak Response',
    description: 'Safety measures during animal disease outbreaks',
    priority: 'medium',
    estimatedTime: '20-30 minutes',
    category: 'Animal Health',
    steps: [
      'Avoid contact with wild or sick animals',
      'Report dead or sick animals to local authorities',
      'Keep pets indoors and up to date on vaccinations',
      'Wear protective equipment when handling animals',
      'Wash hands thoroughly after any animal contact',
      'Do not consume meat or dairy from affected areas',
      'Follow quarantine orders for livestock if applicable',
      'Dispose of animal waste properly',
      'Seek medical attention if bitten or scratched by animals',
      'Follow veterinary guidance for pet protection'
    ]
  },
  {
    id: 'livestock-health-protocol',
    type: 'livestock_health',
    title: 'Livestock Health Emergency Response',
    description: 'Protecting livestock during health emergencies',
    priority: 'medium',
    estimatedTime: '30-60 minutes',
    category: 'Animal Health',
    steps: [
      'Isolate sick animals immediately from healthy ones',
      'Contact veterinarian and animal health authorities',
      'Implement biosecurity measures on property',
      'Restrict movement of animals on and off property',
      'Disinfect equipment, vehicles, and clothing',
      'Monitor all animals for signs of illness',
      'Keep detailed records of animal health and movements',
      'Follow official quarantine and testing protocols',
      'Dispose of deceased animals according to regulations',
      'Coordinate with neighboring farms for area-wide response'
    ]
  },

  // Plant and Agriculture Protocols
  {
    id: 'crop-disease-protocol',
    type: 'crop_disease',
    title: 'Crop Disease Management',
    description: 'Response to agricultural disease outbreaks',
    priority: 'medium',
    estimatedTime: '45-90 minutes',
    category: 'Plant & Agriculture',
    steps: [
      'Identify and report suspected crop diseases to agricultural authorities',
      'Isolate affected areas to prevent spread',
      'Implement quarantine measures for affected crops',
      'Remove and destroy infected plant material safely',
      'Disinfect tools and equipment between uses',
      'Monitor surrounding crops for signs of disease',
      'Follow recommended treatment protocols from experts',
      'Document disease progression and treatment efforts',
      'Coordinate with neighboring farms for area management',
      'Plan alternative crops for affected areas'
    ]
  },
  {
    id: 'invasive-species-protocol',
    type: 'invasive_species',
    title: 'Invasive Species Response',
    description: 'Managing invasive plant and animal species',
    priority: 'low',
    estimatedTime: '60-120 minutes',
    category: 'Plant & Agriculture',
    steps: [
      'Identify and report invasive species to local authorities',
      'Do not attempt to move or transport invasive species',
      'Photograph and document invasive species locations',
      'Follow approved removal methods for specific species',
      'Dispose of invasive species according to regulations',
      'Clean equipment to prevent spread to new areas',
      'Monitor treated areas for re-emergence',
      'Plant native species to restore affected ecosystems',
      'Educate others about invasive species identification',
      'Participate in community removal efforts'
    ]
  },

  // Environmental Protocols
  {
    id: 'pollution-protocol',
    type: 'pollution',
    title: 'Pollution Emergency Response',
    description: 'Actions during environmental pollution incidents',
    priority: 'high',
    estimatedTime: '10-30 minutes',
    category: 'Environmental',
    steps: [
      'Evacuate the immediate area if safe to do so',
      'Avoid contact with contaminated water, soil, or air',
      'Report pollution incidents to environmental authorities',
      'Seek medical attention if exposed to toxic substances',
      'Follow decontamination procedures if exposed',
      'Stay indoors with windows and doors closed',
      'Use bottled water if water supply is contaminated',
      'Avoid eating locally grown food from affected areas',
      'Monitor official updates for safety instructions',
      'Document health effects and exposure for medical records'
    ]
  },
  {
    id: 'climate-change-protocol',
    type: 'climate_change',
    title: 'Climate Change Adaptation Response',
    description: 'Long-term adaptation to climate change impacts',
    priority: 'medium',
    estimatedTime: '60-180 minutes',
    category: 'Environmental',
    steps: [
      'Assess your property for climate change vulnerabilities',
      'Implement water conservation measures',
      'Prepare for increased frequency of extreme weather',
      'Improve home insulation and energy efficiency',
      'Plant drought-resistant and native vegetation',
      'Create emergency plans for heat waves and storms',
      'Install backup power systems if possible',
      'Participate in community resilience planning',
      'Stay informed about local climate projections',
      'Support policies that address climate change'
    ]
  },

  // Technological Protocols
  {
    id: 'cybersecurity-protocol',
    type: 'cybersecurity',
    title: 'Cybersecurity Incident Response',
    description: 'Actions during cyber attacks and data breaches',
    priority: 'high',
    estimatedTime: '15-45 minutes',
    category: 'Technological',
    steps: [
      'Disconnect affected devices from internet immediately',
      'Do not pay ransoms or respond to threatening messages',
      'Change passwords for all important accounts',
      'Enable two-factor authentication where possible',
      'Report cyber incidents to appropriate authorities',
      'Back up important data to secure, offline storage',
      'Scan all devices with updated antivirus software',
      'Monitor financial accounts for unauthorized activity',
      'Document the incident and any losses',
      'Seek professional cybersecurity assistance if needed'
    ]
  },
  {
    id: 'nuclear-threat-protocol',
    type: 'nuclear_threats',
    title: 'Nuclear Emergency Response',
    description: 'Safety measures during nuclear incidents',
    priority: 'high',
    estimatedTime: '5-20 minutes',
    category: 'Technological',
    steps: [
      'Seek immediate shelter in the nearest building',
      'Go to the center of the building, away from windows',
      'Remove contaminated clothing and seal in plastic bags',
      'Shower with soap and water if possible',
      'Stay indoors for at least 24 hours unless told otherwise',
      'Listen to emergency broadcasts for official instructions',
      'Take potassium iodide only if instructed by authorities',
      'Avoid eating or drinking anything from outside',
      'Keep pets indoors and avoid letting them outside',
      'Follow evacuation orders if and when given'
    ]
  },
  {
    id: 'ai-risks-protocol',
    type: 'ai_risks',
    title: 'AI and Technology Risk Management',
    description: 'Managing risks from artificial intelligence systems',
    priority: 'low',
    estimatedTime: '30-60 minutes',
    category: 'Technological',
    steps: [
      'Verify information from AI systems with multiple sources',
      'Be cautious of deepfakes and AI-generated content',
      'Protect personal data from AI data collection',
      'Report malfunctioning AI systems to appropriate authorities',
      'Maintain human oversight of critical AI decisions',
      'Keep manual backups of important automated systems',
      'Stay informed about AI safety developments',
      'Participate in discussions about AI governance',
      'Support transparent and accountable AI development',
      'Prepare for potential job displacement from automation'
    ]
  }
];

export const MOCK_SAFE_ZONES: SafeZone[] = [
  {
    id: 'central-shelter',
    name: 'Central Community Emergency Shelter',
    type: 'shelter',
    coordinates: { lat: 37.7749, lng: -122.4194 },
    capacity: 500,
    currentOccupancy: 120,
    amenities: ['Food Service', 'Medical Care', 'Pet Friendly', 'WiFi', 'Charging Stations'],
    contactInfo: '(555) 123-4567',
    status: 'open',
    lastUpdated: new Date()
  },
  {
    id: 'hospital-main',
    name: 'General Hospital Emergency Center',
    type: 'hospital',
    coordinates: { lat: 37.7849, lng: -122.4094 },
    capacity: 200,
    currentOccupancy: 45,
    amenities: ['Emergency Care', 'Trauma Center', 'Pharmacy', 'Blood Bank'],
    contactInfo: '(555) 234-5678',
    status: 'open',
    lastUpdated: new Date()
  },
  {
    id: 'evacuation-center',
    name: 'Regional Evacuation Center',
    type: 'evacuation-center',
    coordinates: { lat: 37.7649, lng: -122.4294 },
    capacity: 1000,
    currentOccupancy: 800,
    amenities: ['Transportation Hub', 'Information Center', 'Supply Distribution'],
    contactInfo: '(555) 345-6789',
    status: 'open',
    lastUpdated: new Date()
  }
];

export const GUIDE_TEMPLATES: GuideType[] = [
  {
    id: 'complete-emergency-guide',
    title: 'Complete Emergency Preparedness Guide',
    type: 'general',
    content: SAFETY_PROTOCOLS,
    customizable: true,
    languages: ['en', 'es', 'fr'],
    estimatedPages: 25,
    lastUpdated: new Date()
  },
  {
    id: 'quick-reference-card',
    title: 'Emergency Quick Reference Card',
    type: 'quick-reference',
    content: SAFETY_PROTOCOLS.slice(0, 3),
    customizable: false,
    languages: ['en', 'es'],
    estimatedPages: 2,
    lastUpdated: new Date()
  },
  {
    id: 'family-emergency-plan',
    title: 'Family Emergency Plan Template',
    type: 'custom',
    content: [],
    customizable: true,
    languages: ['en'],
    estimatedPages: 8,
    lastUpdated: new Date()
  },
  {
    id: 'earthquake-specific',
    title: 'Earthquake Safety Guide',
    type: 'disaster-specific',
    content: SAFETY_PROTOCOLS.filter(p => p.type === 'earthquake'),
    customizable: true,
    languages: ['en', 'es'],
    estimatedPages: 6,
    lastUpdated: new Date()
  },
  {
    id: 'wildfire-specific',
    title: 'Wildfire Evacuation Guide',
    type: 'disaster-specific',
    content: SAFETY_PROTOCOLS.filter(p => p.type === 'wildfire'),
    customizable: true,
    languages: ['en', 'es'],
    estimatedPages: 8,
    lastUpdated: new Date()
  }
];